import { refreshLabel } from './refresh-label.js';
import { useSiblingReorder } from '../../components/useSiblingReorder.js';
import { Tooltip } from '../../components/Tooltip.js';
import {
  createContext,
  useContext,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
} from 'react';
import { createPortal } from 'react-dom';
import { MenuIcon, type MenuIconName } from '../reports/MenuIcons.js';
import type { Folder } from '../../api/folders.js';
import type { DashboardSummary, StandardCustomerBoards } from '../../api/dashboards.js';
import {
  ChevronDown,
  ChevronRight,
  DashboardIcon,
  FolderIcon,
  KebabIcon,
  KioskIcon,
} from '../../components/icons.js';

/**
 * DashboardFolderTree — same explorer pattern as reports/FolderTree.tsx,
 * specialized for dashboards (DashboardSummary instead of
 * ReportTemplateSummary). Forked rather than generalized because the
 * existing codebase already has two FolderTree forks (templates and
 * report-instances); a `<T>`-generic refactor would require touching
 * all three consumer pages and is tracked as a follow-up.
 *
 * Leaves expose mode + refresh interval as the secondary label so the
 * sidebar tells you at a glance which dashboards are kiosk-class.
 */

export interface DashboardFolderTreeProps {
  folders: ReadonlyArray<Folder>;
  dashboards: ReadonlyArray<DashboardSummary>;
  selectedDashboardId: string | null;
  onSelectDashboard(id: string): void;
  expanded: ReadonlySet<string>;
  onToggle(id: string): void;
  /** Clicking a folder row opens that folder's contents listing in the main
   *  pane (alongside expanding its tree), so a user can click any folder or
   *  header and see what's inside on the right. */
  onSelectFolder?: ((id: string) => void) | undefined;
  searchQuery?: string;
  /**
   * Consumer (embed) mode: the enclosing sidebar section is already titled
   * "Dashboards" and controls this tree, so the static "All dashboards" root
   * row is redundant and hidden. Authoring keeps it as the new-folder anchor.
   */
  consumerMode?: boolean;
  /**
   * Whether the viewer may reorganize the tree. Defaults to true so every
   * authoring surface is unchanged.
   *
   * CSAGE-396: `consumerMode` hides the top toolbar but leaves each row's
   * kebab menu — New folder / Rename / Move / Delete — fully populated, so
   * the Insights embed offered folder management to every user including
   * read-only ones. `POST /folders` requires an author role, so a portal
   * user holding only INSIGHTS_VIEW (→ ReportViewer + DashboardViewer) got
   * a 403 that no mutation surfaced: the prompt accepted a name and the
   * folder simply never appeared. Offer the action only when the server
   * will honour it.
   */
  canOrganize?: boolean;
  /**
   * CSAGE-583: whether the viewer may rename, move or delete a standard
   * dashboard (one board every provisioned customer reads). The server allows
   * it to internal admins only. Defaults to false.
   */
  canOrganizeStandard?: boolean;
  /**
   * CSAGE-586: each customer's standard boards, keyed by lower-cased customer
   * (its top-level folder name). Pass it only to an internal admin at the org
   * root: each matching top-level folder then lists them under a read-only
   * "Standard" group, since those boards live in the standard folder, not
   * the customer's.
   */
  standardByCustomer?: ReadonlyMap<string, StandardCustomerBoards> | undefined;
  /** Open a standard board as that customer sees it. Required with `standardByCustomer`. */
  onOpenStandardAsCustomer?:
    | ((dashboardId: string, customer: StandardCustomerBoards) => void)
    | undefined;
  /** The standard board open as a customer, to highlight its row. */
  selectedStandard?: { dashboardId: string; customer: string } | null | undefined;

  onReorderItems?:
    | ((kind: 'folders' | 'dashboards', ids: string[]) => Promise<unknown>)
    | undefined;
  onRenameFolder(folder: Folder): void;
  onMoveFolder(folder: Folder): void;
  onDeleteFolder(folder: Folder): void;
  onNewFolder(parent: Folder | null): void;
  onNewDashboard(parent: Folder | null): void;

  onRenameDashboard(d: DashboardSummary): void;
  onMoveDashboard(d: DashboardSummary): void;
  onDeleteDashboard(d: DashboardSummary): void;
  /**
   * CSAGE-557: open "Manage access" for a dashboard. Pass it only to sessions
   * that may change audiences; the row menu offers it when it is set.
   */
  onManageAccess?: ((d: DashboardSummary) => void) | undefined;
  /** Reorder a dashboard up or down within its folder. */
  onReorderDashboard(d: DashboardSummary, direction: 'up' | 'down'): void;
  /** Reorder a folder up or down within its parent. */
  onReorderFolder(folder: Folder, direction: 'up' | 'down'): void;
}

interface FolderNode {
  kind: 'folder';
  folder: Folder;
  children: TreeNode[];
  directCount: number;
  subtreeCount: number;
}
interface DashboardNode {
  kind: 'dashboard';
  dashboard: DashboardSummary;
}
/** CSAGE-586: a customer's standard boards, under its top-level folder. Read-only. */
interface StandardGroupNode {
  kind: 'standard';
  folderId: string;
  entry: StandardCustomerBoards;
  boards: StandardCustomerBoards['boards'];
}
type TreeNode = FolderNode | DashboardNode | StandardGroupNode;

const ReorderContext = createContext<(node: TreeNode) => React.HTMLAttributes<HTMLElement>>(
  () => ({}),
);

export function DashboardFolderTree(props: DashboardFolderTreeProps): JSX.Element {
  const {
    folders,
    dashboards,
    selectedDashboardId,
    onSelectDashboard,
    expanded,
    onToggle,
    onSelectFolder,
    searchQuery,
    onRenameFolder,
    onMoveFolder,
    onDeleteFolder,
    onNewFolder,
    onNewDashboard,
    onRenameDashboard,
    onMoveDashboard,
    onDeleteDashboard,
    onManageAccess,
    consumerMode,
    canOrganize,
    canOrganizeStandard,
    standardByCustomer,
    onOpenStandardAsCustomer,
    selectedStandard,
    onReorderDashboard,
    onReorderFolder,
  } = props;

  const reorder = useSiblingReorder(
    props.onReorderItems && (canOrganize ?? true)
      ? async (keys) => {
          const kind = keys[0]?.startsWith('f:') ? 'folders' : 'dashboards';
          await props.onReorderItems!(
            kind,
            keys.map((key) => key.slice(2)),
          );
        }
      : undefined,
  );
  const rowBindings = (node: TreeNode) => {
    if (node.kind === 'standard') return {};
    const siblings =
      node.kind === 'folder'
        ? folders.filter((f) => f.parentId === node.folder.parentId).map((f) => `f:${f.id}`)
        : dashboards.filter((d) => d.folderId === node.dashboard.folderId).map((d) => `d:${d.id}`);
    return reorder.row(keyFor(node), siblings);
  };

  // Empty folders are pruned in consumer/embed mode — EXCEPT for a user who can
  // organize. The Insights embed runs consumerMode + canOrganize together
  // (EmbedInsightsView), so without this an organizer (e.g. a customer admin)
  // creates a folder and it immediately vanishes because it is empty, leaving
  // nowhere to move dashboards into. Keep empty folders visible whenever the
  // viewer may reorganize the tree; a pure read-only consumer still gets them
  // hidden.
  const hideEmpty = (consumerMode ?? false) && !(canOrganize ?? true);
  const tree = useMemo(
    () => buildTree(folders, dashboards, searchQuery ?? '', hideEmpty, standardByCustomer),
    [folders, dashboards, searchQuery, hideEmpty, standardByCustomer],
  );
  const forceExpanded = useMemo<ReadonlySet<string>>(
    () => computeAutoExpand(folders, dashboards, searchQuery ?? '', standardByCustomer),
    [folders, dashboards, searchQuery, standardByCustomer],
  );

  return (
    <ReorderContext.Provider value={rowBindings}>
      <div className="folder-tree" role="tree" aria-busy={reorder.pending}>
        {reorder.message && (
          <p className="navigation-order-status" role="status">
            {reorder.message}
          </p>
        )}
        {!consumerMode && (
          <div className="folder-tree-row folder-tree-row--root">
            <span className="folder-tree-chevron folder-tree-chevron--static">
              <FolderIcon open />
            </span>
            <span className="folder-tree-label">
              <span className="folder-tree-name">All dashboards</span>
              <span className="folder-tree-count">{dashboards.length}</span>
            </span>
            <RowMenu
              items={[
                {
                  label: 'New folder at top',
                  icon: 'folder-plus',
                  action: () => onNewFolder(null),
                },
                { label: 'New dashboard', icon: 'open', action: () => onNewDashboard(null) },
              ]}
            />
          </div>
        )}

        <ul className="folder-tree-list">
          {tree.rootNodes.map((n) => (
            <TreeRow
              key={keyFor(n)}
              node={n}
              depth={0}
              selectedDashboardId={selectedDashboardId}
              onSelectDashboard={onSelectDashboard}
              expanded={expanded}
              forceExpanded={forceExpanded}
              onToggle={onToggle}
              onSelectFolder={onSelectFolder}
              onRenameFolder={onRenameFolder}
              onMoveFolder={onMoveFolder}
              onDeleteFolder={onDeleteFolder}
              onNewFolder={onNewFolder}
              onNewDashboard={onNewDashboard}
              onReorderFolder={onReorderFolder}
              onRenameDashboard={onRenameDashboard}
              onMoveDashboard={onMoveDashboard}
              onDeleteDashboard={onDeleteDashboard}
              onManageAccess={onManageAccess}
              onReorderDashboard={onReorderDashboard}
              canOrganize={canOrganize ?? true}
              canOrganizeStandard={canOrganizeStandard ?? false}
              onOpenStandardAsCustomer={onOpenStandardAsCustomer}
              selectedStandard={selectedStandard}
              consumerMode={consumerMode ?? false}
            />
          ))}
        </ul>
      </div>
    </ReorderContext.Provider>
  );
}

interface TreeRowProps extends Omit<
  DashboardFolderTreeProps,
  'folders' | 'dashboards' | 'searchQuery'
> {
  node: TreeNode;
  depth: number;
  forceExpanded: ReadonlySet<string>;
}

function TreeRow(props: TreeRowProps): JSX.Element {
  if (props.node.kind === 'dashboard') return <DashboardLeaf {...props} node={props.node} />;
  if (props.node.kind === 'standard') return <StandardGroupRow {...props} node={props.node} />;
  return <FolderRow {...props} node={props.node} />;
}

function FolderRow({
  node,
  depth,
  expanded,
  forceExpanded,
  onToggle,
  onSelectFolder,
  selectedDashboardId,
  onSelectDashboard,
  onRenameFolder,
  onMoveFolder,
  onDeleteFolder,
  onNewFolder,
  onNewDashboard,
  onReorderFolder,
  onRenameDashboard,
  onMoveDashboard,
  onDeleteDashboard,
  onManageAccess,
  onReorderDashboard,
  canOrganize,
  canOrganizeStandard,
  onOpenStandardAsCustomer,
  selectedStandard,
  consumerMode,
}: TreeRowProps & { node: FolderNode }): JSX.Element {
  const bindings = useContext(ReorderContext)(node);
  const mayOrganize = canOrganize ?? true;
  const f = node.folder;
  const hasChildren = node.children.length > 0;
  const isOpen = expanded.has(f.id) || forceExpanded.has(f.id);
  const indent: CSSProperties = { paddingLeft: `${8 + depth * 22}px` };

  // In authoring mode empty folders are kept (buildTree does not prune them)
  // so a newly created folder is visible before any dashboard lands in it; in
  // consumer/embed mode buildTree prunes them, so this row only renders for a
  // folder that has content.
  return (
    <li role="treeitem" aria-expanded={hasChildren ? isOpen : undefined}>
      <div {...bindings} className={`folder-tree-row ${bindings.className ?? ''}`} style={indent}>
        <button
          type="button"
          className="folder-tree-chevron"
          onClick={() => hasChildren && onToggle(f.id)}
          aria-label={isOpen ? 'Collapse' : 'Expand'}
          disabled={!hasChildren}
        >
          {hasChildren ? (
            isOpen ? (
              <ChevronDown />
            ) : (
              <ChevronRight />
            )
          ) : (
            <span className="folder-tree-dot">·</span>
          )}
        </button>
        <Tooltip content={f.name} when={nameIsCutOff}>
          {(tip) => (
            <button
              type="button"
              className="folder-tree-label folder-tree-label--folder"
              onClick={() => {
                // Clicking a folder opens its contents listing in the main pane
                // (CSAGE-472 parity with Analytics), and expands it in the tree so
                // its children are revealed. The chevron still toggles collapse.
                onSelectFolder?.(f.id);
                if (hasChildren && !isOpen) onToggle(f.id);
              }}
              {...tip}
            >
              <span className="folder-tree-icon">
                <FolderIcon open={isOpen} />
              </span>
              <span className="folder-tree-name">{f.name}</span>
              <span className="folder-tree-count">{node.subtreeCount}</span>
            </button>
          )}
        </Tooltip>
        {bindings.draggable !== undefined && (
          <span
            className="navigation-drag-handle"
            title="Drag to reorder · Alt + ↑ / ↓"
            aria-hidden="true"
          >
            ⠿
          </span>
        )}
        {/* CSAGE-396: every item here is a write the server gates on an
            author role. Showing them to a read-only viewer produced silent
            403s, so the menu is omitted entirely rather than offering
            actions that cannot succeed. */}
        {mayOrganize && (
          <RowMenu
            items={[
              ...(!consumerMode
                ? [
                    {
                      label: 'New dashboard here',
                      icon: 'open' as const,
                      action: () => onNewDashboard(f),
                    },
                  ]
                : []),
              { label: 'New folder inside', icon: 'folder-plus', action: () => onNewFolder(f) },
              ...(!consumerMode
                ? [
                    {
                      label: 'Move up',
                      icon: 'move' as const,
                      action: () => onReorderFolder(f, 'up'),
                    },
                    {
                      label: 'Move down',
                      icon: 'move' as const,
                      action: () => onReorderFolder(f, 'down'),
                    },
                  ]
                : []),
              { label: 'Rename…', icon: 'rename', action: () => onRenameFolder(f) },
              { label: 'Move…', icon: 'move', action: () => onMoveFolder(f) },
              {
                label: 'Delete folder',
                icon: 'delete',
                action: () => onDeleteFolder(f),
                destructive: true,
              },
            ]}
          />
        )}
      </div>
      {isOpen && hasChildren && (
        <ul className="folder-tree-list">
          {node.children.map((c) => (
            <TreeRow
              key={keyFor(c)}
              node={c}
              depth={depth + 1}
              expanded={expanded}
              forceExpanded={forceExpanded}
              onToggle={onToggle}
              onSelectFolder={onSelectFolder}
              selectedDashboardId={selectedDashboardId}
              onSelectDashboard={onSelectDashboard}
              onRenameFolder={onRenameFolder}
              onMoveFolder={onMoveFolder}
              onDeleteFolder={onDeleteFolder}
              onNewFolder={onNewFolder}
              onNewDashboard={onNewDashboard}
              onReorderFolder={onReorderFolder}
              onRenameDashboard={onRenameDashboard}
              onMoveDashboard={onMoveDashboard}
              onDeleteDashboard={onDeleteDashboard}
              onManageAccess={onManageAccess}
              onReorderDashboard={onReorderDashboard}
              canOrganize={canOrganize ?? true}
              canOrganizeStandard={canOrganizeStandard ?? false}
              onOpenStandardAsCustomer={onOpenStandardAsCustomer}
              selectedStandard={selectedStandard}
              consumerMode={consumerMode ?? false}
            />
          ))}
        </ul>
      )}
    </li>
  );
}

/**
 * CSAGE-586: the standard boards a customer is served, under its folder. The
 * boards live in the standard folder, so nothing here moves, renames or
 * deletes them, and no row is a drag source or target: opening one shows it
 * as this customer sees it.
 */
function StandardGroupRow({
  node,
  depth,
  expanded,
  forceExpanded,
  onToggle,
  onOpenStandardAsCustomer,
  selectedStandard,
}: TreeRowProps & { node: StandardGroupNode }): JSX.Element {
  const key = standardGroupKey(node.folderId);
  const isOpen = expanded.has(key) || forceExpanded.has(key);
  const customer = node.entry.customer;
  return (
    <li role="treeitem" aria-expanded={isOpen}>
      <div
        className="folder-tree-row folder-tree-row--standard-ref"
        style={{ paddingLeft: `${8 + depth * 22}px` }}
      >
        <button
          type="button"
          className="folder-tree-chevron"
          onClick={() => onToggle(key)}
          aria-label={isOpen ? 'Collapse' : 'Expand'}
        >
          {isOpen ? <ChevronDown /> : <ChevronRight />}
        </button>
        <button
          type="button"
          className="folder-tree-label folder-tree-label--folder"
          onClick={() => onToggle(key)}
          title={`Standard dashboards ${customer} is provisioned for. They live in the standard folder; open one to see it as ${customer} does.`}
        >
          <span className="folder-tree-icon">
            <FolderIcon open={isOpen} />
          </span>
          <span className="folder-tree-name">Standard</span>
          <span className="folder-tree-count">{node.boards.length}</span>
        </button>
      </div>
      {isOpen && (
        <ul className="folder-tree-list">
          {node.boards.map((b) => {
            const isSelected =
              selectedStandard?.dashboardId === b.dashboardId &&
              selectedStandard.customer.toLowerCase() === customer.toLowerCase();
            return (
              <li key={b.dashboardId} role="treeitem" aria-selected={isSelected}>
                <div
                  className={`folder-tree-row folder-tree-row--leaf folder-tree-row--standard-ref ${isSelected ? 'folder-tree-row--selected' : ''}`}
                  style={{ paddingLeft: `${32 + (depth + 1) * 22}px` }}
                >
                  <button
                    type="button"
                    className="folder-tree-label folder-tree-label--template"
                    onClick={() => onOpenStandardAsCustomer?.(b.dashboardId, node.entry)}
                    title={`Open as ${customer}`}
                  >
                    <span className="folder-tree-icon folder-tree-icon--template">
                      <DashboardIcon />
                    </span>
                    <span className="folder-tree-name">{b.name}</span>
                    {b.restricted && (
                      <span
                        className="folder-tree-badge folder-tree-badge--restricted"
                        title={`Restricted: only the roles or users listed for ${customer} see it`}
                      >
                        Restricted
                      </span>
                    )}
                    {b.customized && (
                      <span
                        className="folder-tree-badge folder-tree-badge--custom"
                        title={`${customer} has its own settings for this board`}
                      >
                        Custom
                      </span>
                    )}
                  </button>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </li>
  );
}

function DashboardLeaf({
  node,
  depth,
  selectedDashboardId,
  onSelectDashboard,
  onRenameDashboard,
  onMoveDashboard,
  onDeleteDashboard,
  onManageAccess,
  onReorderDashboard,
  canOrganize,
  canOrganizeStandard,
  consumerMode,
}: TreeRowProps & { node: DashboardNode }): JSX.Element {
  const bindings = useContext(ReorderContext)(node);
  const d = node.dashboard;
  // CSAGE-583: a standard board is the same board for every customer, so
  // only those the server lets change it (internal admins) are offered to.
  const mayOrganize = (canOrganize ?? true) && (!d.standard || (canOrganizeStandard ?? false));
  const isSelected = selectedDashboardId === d.id;
  const indent: CSSProperties = { paddingLeft: `${32 + depth * 22}px` };
  const isKiosk = d.displayMode === 'kiosk';
  // Display mode and refresh cadence are authoring metadata. In the customer
  // embed (consumerMode) the tooltip is the name plus the description, and
  // the KIOSK/DASH badge is not shown — as in the Insights design preview.
  const meta = `${d.displayMode} · ${refreshLabel(d.refreshIntervalSec)}`;
  const tooltip = consumerMode
    ? d.description
      ? `${d.name}\n\n${d.description}`
      : d.name
    : d.description
      ? `${d.name}\n\n${d.description}\n\n${meta}`
      : `${d.name} · ${meta}`;
  // The internal note (CSAGE-577) is author-only and never part of this text.
  const hasExtra = !consumerMode || Boolean(d.description);
  return (
    <li role="treeitem" aria-selected={isSelected}>
      <div
        {...bindings}
        className={`${bindings.className ?? ''} folder-tree-row folder-tree-row--leaf ${isSelected ? 'folder-tree-row--selected' : ''}`}
        style={indent}
      >
        <Tooltip
          content={tooltip}
          // CSAGE-577: show only when it adds something — a cut-off name, a
          // description, or (authoring) the mode and refresh hint.
          when={(el) => hasExtra || nameIsCutOff(el)}
        >
          {(tip) => (
            <button
              type="button"
              className="folder-tree-label folder-tree-label--template"
              onClick={() => onSelectDashboard(d.id)}
              {...tip}
            >
              <span className="folder-tree-icon folder-tree-icon--template">
                {isKiosk ? <KioskIcon /> : <DashboardIcon />}
              </span>
              <span className="folder-tree-name">{d.name}</span>
              {!consumerMode && d.standard && (
                <span
                  className="folder-tree-badge folder-tree-badge--standard"
                  title="Standard: one board served to every provisioned customer"
                >
                  STD
                </span>
              )}
              {!consumerMode && (
                <span
                  className={`folder-tree-badge folder-tree-badge--${isKiosk ? 'kiosk' : 'dashboard'}`}
                  title={
                    isKiosk
                      ? 'Kiosk view — full-screen, refresh on cadence'
                      : 'Interactive dashboard'
                  }
                >
                  {isKiosk ? 'KIOSK' : 'DASH'}
                </span>
              )}
            </button>
          )}
        </Tooltip>
        {bindings.draggable !== undefined && (
          <span
            className="navigation-drag-handle"
            title="Drag to reorder · Alt + ↑ / ↓"
            aria-hidden="true"
          >
            ⠿
          </span>
        )}
        {/* CSAGE-396: "Open" is a read and stays for everyone; the rest are
            writes the server gates on an author role, so a read-only viewer
            is not offered them. */}
        <RowMenu
          items={[
            { label: 'Open', icon: 'open', action: () => onSelectDashboard(d.id) },
            // CSAGE-557: who may see it; set only for sessions that may change that.
            ...(onManageAccess
              ? [
                  {
                    label: 'Manage access…',
                    icon: 'people' as const,
                    action: () => onManageAccess(d),
                  },
                ]
              : []),
            ...(mayOrganize
              ? ([
                  ...(!consumerMode
                    ? [
                        {
                          label: 'Move up',
                          icon: 'move' as const,
                          action: () => onReorderDashboard(d, 'up'),
                        },
                        {
                          label: 'Move down',
                          icon: 'move' as const,
                          action: () => onReorderDashboard(d, 'down'),
                        },
                      ]
                    : []),
                  { label: 'Rename…', icon: 'rename', action: () => onRenameDashboard(d) },
                  { label: 'Move to…', icon: 'move', action: () => onMoveDashboard(d) },
                  {
                    label: 'Delete dashboard',
                    icon: 'delete',
                    action: () => onDeleteDashboard(d),
                    destructive: true,
                  },
                ] as const)
              : []),
          ]}
        />
      </div>
    </li>
  );
}

// ── Hover kebab menu — identical to reports/FolderTree, kept local so
// the dashboards module isn't coupled to that file's internals.

interface MenuItem {
  label: string;
  action(): void;
  destructive?: boolean;
  icon?: MenuIconName;
}

function RowMenu({ items }: { items: ReadonlyArray<MenuItem> }): JSX.Element {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);
  const btnRef = useRef<HTMLButtonElement | null>(null);
  const popRef = useRef<HTMLDivElement | null>(null);

  const close = useCallback(() => setOpen(false), []);

  useLayoutEffect(() => {
    if (!open || !btnRef.current) return;
    const r = btnRef.current.getBoundingClientRect();
    const popWidth = 180;
    setPos({ top: r.bottom + 2, left: Math.max(8, r.right - popWidth) });
  }, [open]);

  useEffect(() => {
    if (!open) return;
    function onDocClick(e: MouseEvent): void {
      const tgt = e.target as globalThis.Node | null;
      if (tgt && popRef.current?.contains(tgt)) return;
      if (tgt && btnRef.current?.contains(tgt)) return;
      close();
    }
    function onEsc(e: KeyboardEvent): void {
      if (e.key === 'Escape') close();
    }
    function onLayoutChange(): void {
      close();
    }
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onEsc);
    window.addEventListener('scroll', onLayoutChange, true);
    window.addEventListener('resize', onLayoutChange);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onEsc);
      window.removeEventListener('scroll', onLayoutChange, true);
      window.removeEventListener('resize', onLayoutChange);
    };
  }, [open, close]);

  const popover =
    open && pos
      ? createPortal(
          <div
            ref={popRef}
            className="folder-tree-menu-pop folder-tree-menu-pop--portaled"
            role="menu"
            style={{ position: 'fixed', top: pos.top, left: pos.left }}
          >
            {items.map((it) => (
              <button
                key={it.label}
                type="button"
                role="menuitem"
                className={`folder-tree-menu-item ${it.destructive ? 'folder-tree-menu-item--danger' : ''}`}
                onClick={(e) => {
                  e.stopPropagation();
                  close();
                  it.action();
                }}
              >
                <span className="folder-tree-menu-icon">
                  {it.icon ? <MenuIcon name={it.icon} /> : null}
                </span>
                <span>{it.label}</span>
              </button>
            ))}
          </div>,
          document.body,
        )
      : null;

  return (
    <div className="folder-tree-menu">
      <button
        ref={btnRef}
        type="button"
        className="folder-tree-menu-trigger"
        onClick={(e) => {
          e.stopPropagation();
          setOpen((v) => !v);
        }}
        aria-label="Actions"
      >
        <KebabIcon />
      </button>
      {popover}
    </div>
  );
}

// ── Icons ───────────────────────────────────────────────────────────────

// ── Tree assembly ──────────────────────────────────────────────────────

interface BuiltTree {
  rootNodes: TreeNode[];
}

function buildTree(
  folders: ReadonlyArray<Folder>,
  dashboards: ReadonlyArray<DashboardSummary>,
  searchQuery: string,
  hideEmpty: boolean,
  standardByCustomer?: ReadonlyMap<string, StandardCustomerBoards>,
): BuiltTree {
  const q = searchQuery.trim().toLowerCase();
  const matches = (s: string): boolean => !q || s.toLowerCase().includes(q);

  const folderNodes = new Map<string, FolderNode>();
  for (const f of folders) {
    folderNodes.set(f.id, {
      kind: 'folder',
      folder: f,
      children: [],
      directCount: 0,
      subtreeCount: 0,
    });
  }

  const rootFolders: FolderNode[] = [];
  for (const f of folders) {
    const node = folderNodes.get(f.id)!;
    if (f.parentId && folderNodes.has(f.parentId)) {
      folderNodes.get(f.parentId)!.children.push(node);
    } else {
      rootFolders.push(node);
    }
  }

  // Folders holding at least one dashboard the viewer can see, search aside.
  // A read-only viewer's search must not surface a folder its unfiltered tree
  // hides (see the prune note below).
  const nonEmpty = new Set<string>();
  for (const d of dashboards) {
    let cursor = d.folderId ? folderNodes.get(d.folderId) : undefined;
    while (cursor && !nonEmpty.has(cursor.folder.id)) {
      nonEmpty.add(cursor.folder.id);
      cursor = cursor.folder.parentId ? folderNodes.get(cursor.folder.parentId) : undefined;
    }
  }

  const topLevelDashboardLeaves: DashboardNode[] = [];
  for (const d of dashboards) {
    if (!matches(d.name)) continue;
    const leaf: DashboardNode = { kind: 'dashboard', dashboard: d };
    if (d.folderId && folderNodes.has(d.folderId)) {
      const host = folderNodes.get(d.folderId)!;
      host.children.push(leaf);
      host.directCount += 1;
    } else {
      topLevelDashboardLeaves.push(leaf);
    }
  }

  const computeSubtree = (n: FolderNode): number => {
    let total = n.directCount;
    for (const c of n.children) {
      if (c.kind === 'folder') total += computeSubtree(c);
    }
    n.subtreeCount = total;
    return total;
  };
  for (const r of rootFolders) computeSubtree(r);

  const sortSiblings = (arr: TreeNode[]): void => {
    arr.sort((a, b) => {
      if (a.kind !== b.kind) return a.kind === 'folder' ? -1 : 1;
      return 0;
    });
  };
  const sortAll = (n: FolderNode): void => {
    sortSiblings(n.children);
    for (const c of n.children) if (c.kind === 'folder') sortAll(c);
  };
  for (const r of rootFolders) sortAll(r);

  // CSAGE-586: a customer's standard boards head its top-level folder. They are
  // not the folder's own, so they stay out of its count; they do keep the
  // folder from being pruned as empty.
  const withStandard = new Set<string>();
  if (standardByCustomer) {
    for (const r of rootFolders) {
      const entry = standardByCustomer.get(r.folder.name.toLowerCase());
      const boards = entry?.boards.filter((b) => matches(b.name)) ?? [];
      if (!entry || boards.length === 0) continue;
      r.children.unshift({ kind: 'standard', folderId: r.folder.id, entry, boards });
      withStandard.add(r.folder.id);
    }
  }

  // Empty folders are pruned when searching (so only matches show) and in
  // consumer/embed mode (`hideEmpty`) — a read-only viewer should not see
  // folders with nothing in them, e.g. a share-scoped customer's empty
  // ESS/Devices/Actions folders. Authoring, AND any organizer in the embed
  // (consumerMode + canOrganize), keep them so a newly created folder is
  // visible before any dashboard lands in it — otherwise the organizer has
  // nowhere to move dashboards into (see the hideEmpty note at the call site
  // and the TreeRow note). A searched-for empty folder still shows by name
  // match, but only to someone who would see it without searching: under
  // `hideEmpty` a name match on a folder with no visible dashboards stays
  // hidden, or search would list folders the unfiltered tree prunes.
  const prune = (nodes: TreeNode[]): TreeNode[] =>
    nodes.filter((n) => {
      if (n.kind !== 'folder') return true;
      n.children = prune(n.children);
      if (n.subtreeCount > 0 || withStandard.has(n.folder.id)) return true;
      if (hideEmpty && !nonEmpty.has(n.folder.id)) return false;
      return q.length > 0 && matches(n.folder.name);
    });
  const rootNodes: TreeNode[] =
    q.length > 0 || hideEmpty
      ? prune([...rootFolders, ...topLevelDashboardLeaves])
      : [...rootFolders, ...topLevelDashboardLeaves];
  sortSiblings(rootNodes);

  return { rootNodes };
}

function computeAutoExpand(
  folders: ReadonlyArray<Folder>,
  dashboards: ReadonlyArray<DashboardSummary>,
  searchQuery: string,
  standardByCustomer?: ReadonlyMap<string, StandardCustomerBoards>,
): ReadonlySet<string> {
  const q = searchQuery.trim().toLowerCase();
  if (!q) return new Set();

  const byId = new Map<string, Folder>();
  for (const f of folders) byId.set(f.id, f);
  const ancestors = (id: string | null): string[] => {
    const out: string[] = [];
    let cursor: string | null = id;
    while (cursor) {
      out.push(cursor);
      const f = byId.get(cursor);
      cursor = f ? f.parentId : null;
    }
    return out;
  };

  const open = new Set<string>();
  for (const d of dashboards) {
    if (d.name.toLowerCase().includes(q)) {
      for (const a of ancestors(d.folderId)) open.add(a);
    }
  }
  for (const f of folders) {
    if (f.name.toLowerCase().includes(q)) {
      for (const a of ancestors(f.id)) open.add(a);
    }
  }
  if (standardByCustomer) {
    for (const f of folders) {
      if (f.parentId) continue;
      const entry = standardByCustomer.get(f.name.toLowerCase());
      if (entry?.boards.some((b) => b.name.toLowerCase().includes(q))) {
        open.add(f.id);
        open.add(standardGroupKey(f.id));
      }
    }
  }
  return open;
}

function keyFor(n: TreeNode): string {
  if (n.kind === 'standard') return `s:${n.folderId}`;
  return n.kind === 'folder' ? `f:${n.folder.id}` : `d:${n.dashboard.id}`;
}

/** The expand-state id of a customer's standard group (shares the folders' set). */
function standardGroupKey(folderId: string): string {
  return `std:${folderId}`;
}

/** CSAGE-577: true when the row's name text is wider than its box (shown with an ellipsis). */
function nameIsCutOff(label: HTMLElement): boolean {
  const name = label.querySelector<HTMLElement>('.folder-tree-name');
  return name !== null && name.scrollWidth > name.clientWidth;
}
